import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { message, Select, Spin } from "antd";
import { BiImport } from "react-icons/bi";
import { MdPublish } from "react-icons/md";
import { PiExportBold, PiWarningCircleLight } from "react-icons/pi";

import './style.scss'
import uris from "../../../config/uris";
import CustomModal from "../../../assets/component/modal";
import CustomTable from "../../../assets/component/table";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import { antIcon } from "../../custom-playbook/manage-playbook";
import { setFilterValueNavigationState } from "../../../store/slices/gemini/filter-value";
import { PublishTaskAction, publishTaskActionReset } from "../../../store/slices/gemini/publish-task";
import { exportExtractionAction, exportExtractionReset } from "../../../store/slices/gemini/export-extraction";
import { ImportCorrectedFile, importCorrectedFileReset } from "../../../store/slices/gemini/import-corrected-file";
import { entityList, GetItemLevelData, itemlevelDataReset } from "../../../store/slices/gemini/get-entities";

const menuData = {
    title: 'Output Review',
    parentMenu: {
        name: 'Review',
        link: uris?.entityList,
    },
    subMenu: {
        name: 'Output Review',
        link: uris?.outputReview,
    },
}

const tableHeaderData = [
    {
        title: 'Entity Name',
        dataIndex: 'entityName',
        key: 'entityName',
        width: '20%',
        sorter: (a, b) => a.entityName.localeCompare(b.entityName),
    },
    {
        title: 'Assessment Type',
        dataIndex: 'assessmentType',
        key: 'assessmentType',
        width: '10%',
        sorter: (a, b) => a.assessmentType.localeCompare(b.assessmentType),
    },
    {
        title: 'Item ID',
        dataIndex: 'item_id',
        key: 'item_id',
        width: '10%',
        sorter: {
            compare: (a, b) => a.item_id_raw - b.item_id_raw,
            multiple: 3,
        },
        render: (_, record) => record.item_id,
    },
    {
        title: 'Success Count',
        dataIndex: 'successCount',
        key: 'successCount',
        width: '10%',
        sorter: {
            compare: (a, b) => a.successCount - b.successCount,
            multiple: 3,
        },
    },
    {
        title: 'Failed Count',
        dataIndex: 'failureCount',
        key: 'failureCount',
        width: '10%',
        sorter: {
            compare: (a, b) => a.failureCount - b.failureCount,
            multiple: 3,
        },
    },
    {
        title: 'Webpage Count',
        dataIndex: 'webpageCount',
        key: 'webpageCount',
        width: '13%',
        sorter: {
            compare: (a, b) => a.webpageCount - b.webpageCount,
            multiple: 3,
        },
    },
    {
        title: 'Parent',
        dataIndex: 'question',
        key: 'question',
        width: '10%',
        sorter: (a, b) => a.question.localeCompare(b.question),
    },
    // {
    //     title: 'Conflict Count',
    //     dataIndex: 'conflictCount',
    //     key: 'conflictCount',
    //     width: '10%',
    //     sorter: (a, b) => a.conflictCount.localeCompare(b.conflictCount),
    // },
    {
        title: 'Batch Status',
        dataIndex: 'batchStatus',
        key: 'batchStatus',
        width: '15%',
        sorter: (a, b) => a.batchStatus.localeCompare(b.batchStatus),
    },
    {
        title: 'Publish',
        dataIndex: 'publishTask',
        key: 'publishTask',
        width: '15%',
        sorter: (a, b) => a.publishTask.localeCompare(b.publishTask),
    },
]

export const ConfirmationModal = ({ message, handleClose, handleConfirm, isLoading }) => {
    return (
        <div className="confirmation-modal-content">
            <p className="modal-message">{message}</p>
            <div className="action-buttons">
                <div className="button-wrapper yes" onClick={handleConfirm}>
                    {isLoading ? <Spin className="export" indicator={antIcon} /> : "Yes"}
                </div>
                <div className="button-wrapper no" onClick={handleClose}>
                    No
                </div>
            </div>
        </div>
    );
};

const OutputReview = () => {

    const [showFilters, setShowFilters] = useState(true)
    const [selectedEntity, setSelectedEntity] = useState([])
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(50)
    const [selectedRowKeys, setSelectedRowKeys] = useState([])
    const [selectedRows, setSelectedRows] = useState([])
    const [selectedBatchStatus, setSelectedBatchStatus] = useState(null)
    const [selectedAssessmentType, setSelectedAssessmentType] = useState(null)
    const [formattedTableData, setFormattedTableData] = useState({ dataSource: [], columns: [] })
    const [trackExportStatus, setTrackExportStatus] = useState({ all: false, conflict: false, exportAll: false, exportConflictsAll: false });
    const [formDataFile, setFormDataFile] = useState("");
    const [importFileName, setImportFileName] = useState("");
    const [showImportModal, setShowImportModal] = useState(false);
    const [selectedPublishTaskStatus, setSelectedPublishTaskStatus] = useState(null);
    const [shouldReload, setShouldReload] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({
        entity_Ids: [],
        batchExtractionStatus: "",
        assessmentType: [],
        publishStatus: ""
    });

    const { data: entityDropdownData, isLoading: entityLoading } = useSelector((state) => state?.entityList)
    const { data: itemLevelData, isLoading: itemLevelDataLoading } = useSelector((state) => state?.itemLevelData) || {}
    const { success: exportExtractionSuccess, error: exportExtractionError, isLoading: exportExtractionLoading } = useSelector((state) => state?.exportExtraction) || {}
    const { data: importCorrectedFileData, success: importCorrectedFileSuccess, isLoading: importCorrectedFileLoading, error: importCorrectedFileError } = useSelector((state) => state?.importCorrectedFile) || {}
    const { data: publishTaskData, success: publishTaskSuccess, isLoading: publishTaskLoading, error: publishTaskError } = useSelector((state) => state?.publishTask) || {}

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const fileInputRef = useRef();

    const batchStatusOptions = [
        { label: "Completed", value: "Completed" },
        { label: "In Progress", value: "In-Progress" },
        { label: "Pending", value: "Pending" },
        { label: "Failed", value: "Failed" },
    ]

    const assessmentTypeOptions = [
        { label: "Asset", value: "Asset" },
        { label: "Fund", value: "Fund" },
        { label: "Real-Estate", value: "GRESB Real Estate" }
    ]

    const publishTaskOptions = [
        { label: "Pending", value: "Pending" },
        { label: "Published", value: "Published" },
        { label: "Delivered", value: "Delivered" },
    ]

    const handleItemId = (value) => {
        const selectedObject = itemLevelData?.responseData?.find(item => item.item_id === value)
        dispatch(setFilterValueNavigationState({
            entity: [{ label: selectedObject?.entityName, value: selectedObject?.entity_ObjectId }],
            variable: [selectedObject?.question],
        }));
        navigate("/review-entities");
    }

    const triggerReload = () => setShouldReload(prev => !prev);

    const handleBatchStatus = (e) => {
        setSelectedBatchStatus(e)
    }

    const onSelectChange = (newSelectedRowKeys, newSelectedRows) => {
        setSelectedRowKeys(newSelectedRowKeys)
        setSelectedRows(newSelectedRows)
    }

    const rowSelection = {
        preserveSelectedRowKeys: true,
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
            disabled: record.batchStatus !== "Completed",
        })
    }

    const tableStructuredData = (tableData) => {
        const formattedData = tableData.map((entity) => {
            return {
                key: entity.item_id,
                entityName: entity?.entityName || "-",
                assessmentType: entity?.assessment_type || "-",
                item_id_raw: entity?.item_id,
                item_id: (<div className="item-id" onClick={() => handleItemId(entity?.item_id)}>{entity?.item_id || "-"}</div>),
                successCount: entity?.successCount || "-",
                failureCount: entity?.failureCount || "-",
                webpageCount: entity?.webpageCount || "-",
                question: entity?.question || "-",
                conflictCount: entity?.conflictCount || "-",
                batchStatus: entity?.batchStatus || "-",
                publishTask: entity?.isPublished || "-"
            }
        })
        return formattedData;
    }

    const handleEntityChange = (value) => {
        setSelectedEntity(value)
    }

    const handleAssessmentTypeChange = (e) => {
        setSelectedAssessmentType(e)
    }

    const handleExportReport = (exportType) => {
        if (importCorrectedFileLoading) return;
        if (selectedBatchStatus && selectedBatchStatus !== "Completed") return;
        if (selectedRowKeys?.length > 0 && (exportType === "exportAll" || exportType === "exportConflictsAll")) return;
        if (selectedRowKeys?.length === 0 && (exportType === "All" || exportType === "Conflict")) return;
        if (exportType === "All" && trackExportStatus?.all && exportExtractionLoading) return;
        if (exportType === "Conflict" && trackExportStatus?.conflict && exportExtractionLoading) return;
        if (exportType === "exportAll" && trackExportStatus?.exportAll && exportExtractionLoading) return;
        if (exportType === "exportConflictsAll" && trackExportStatus?.exportConflictsAll && exportExtractionLoading) return;
        dispatch(exportExtractionReset());
        const payload = {
            item_ids: selectedRowKeys || [],
            ...(exportType === "Conflict" && { isConflict: true }),
            ...(exportType === "exportAll" && { ...appliedFilters }),
            ...(exportType === "exportConflictsAll" && { isConflict: true, ...appliedFilters })
        }
        dispatch(exportExtractionAction(payload));

        if (exportType === "All") {
            setTrackExportStatus((prevValue) => {
                return { ...prevValue, all: true }
            });
        } else if (exportType === "Conflict") {
            setTrackExportStatus((prevValue) => {
                return { ...prevValue, conflict: true }
            });
        } else if (exportType === "exportAll") {
            setTrackExportStatus((prevValue) => {
                return { ...prevValue, exportAll: true }
            });
        } else if (exportType === "exportConflictsAll") {
            setTrackExportStatus((prevValue) => {
                return { ...prevValue, exportConflictsAll: true }
            });
        }
    }

    const handleImport = () => {
        if (fileInputRef) {
            fileInputRef.current.click();
        }
    }

    const handlePublishTask = () => {
        if (selectedRowKeys?.length === 0) return;
        if (publishTaskLoading) return;
        const payload = {
            item_ids: selectedRowKeys,
        }
        dispatch(PublishTaskAction(payload))
    }

    const handleImportFile = async (event) => {
        const file = event.target.files[0];
        event.target.value = "";

        if (file) {
            const fileType = file.type;
            if (
                fileType ===
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            ) {
                setImportFileName(file.name);
                let payload = new FormData();
                payload.append("file", file);
                setFormDataFile(payload)
                setShowImportModal(true);
            } else {
                message.error("Please select a valid .xlsx file");
            }
        }
    };

    const handleApplyFilters = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
        dispatch(itemlevelDataReset());
        if (itemLevelDataLoading) return;
        if (selectedEntity?.length === 0 && !selectedBatchStatus && !selectedPublishTaskStatus && !selectedAssessmentType) {
            message.error("Please select at least one filter")
            return;
        };
        const filters = {
            entity_Ids: selectedEntity ? selectedEntity : [],
            batchExtractionStatus: selectedBatchStatus || "",
            assessmentType: selectedAssessmentType ? [selectedAssessmentType] : [],
            publishStatus: selectedPublishTaskStatus || ""
        };
        setAppliedFilters(filters);
        setPage(1);
    };

    const handleResetFilters = () => {
        dispatch(itemlevelDataReset())
        setSelectedEntity([]);
        setSelectedBatchStatus(null);
        setSelectedAssessmentType(null);
        setSelectedPublishTaskStatus(null);
        setAppliedFilters({
            entity_Ids: [],
            batchExtractionStatus: "",
            assessmentType: [],
            publishStatus: ""
        });
        setSelectedRowKeys([]);
        setSelectedRows([]);
        setPage(1);
    };

    const handlePublishTaskStatusChange = (e) => {
        setSelectedPublishTaskStatus(e)
    }

    const handleConfirmImport = () => {
        setShowImportModal(false);
        dispatch(ImportCorrectedFile(formDataFile));
    };

    useEffect(() => {
        dispatch(entityList());
    }, []);

    useEffect(() => {
        const payload = {
            page,
            limit,
            ...appliedFilters
        };
        dispatch(GetItemLevelData(payload));
    }, [page, limit, appliedFilters, shouldReload]);

    useEffect(() => {
        if (itemLevelData?.responseData) {
            const formattedTableResponse = itemLevelData?.responseData
                ? tableStructuredData(itemLevelData?.responseData)
                : [];
            setFormattedTableData({
                dataSource: formattedTableResponse,
                columns: tableHeaderData
            })
        }
    }, [itemLevelData?.responseData])

    useEffect(() => {
        if (exportExtractionError?.message) {
            message.error(exportExtractionError?.message);
            dispatch(exportExtractionReset())
            setTrackExportStatus({ all: false, conflict: false });
        }
        if (exportExtractionSuccess) {
            dispatch(exportExtractionReset())
            setTrackExportStatus({ all: false, conflict: false });
        }

    }, [exportExtractionError, exportExtractionSuccess])

    useEffect(() => {
        if (importCorrectedFileError) {
            message.error(importCorrectedFileError?.message);
            dispatch(importCorrectedFileReset());
        } else if (importCorrectedFileSuccess) {
            message.success(importCorrectedFileData?.message)
            dispatch(importCorrectedFileReset());
            setImportFileName("");
            setFormDataFile("");
        }
    }, [importCorrectedFileError, importCorrectedFileSuccess])

    useEffect(() => {
        if (publishTaskError) {
            message.error(publishTaskError?.message)
            dispatch(publishTaskActionReset())
        } else if (publishTaskSuccess) {
            message.success(publishTaskData?.message)
            dispatch(publishTaskActionReset())
            triggerReload();
        }
    }, [publishTaskError, publishTaskSuccess])

    return (
        <div className="review-entities-main">
            <Breadcrumbs breadcrumbDetails={menuData} />
            <div className="review-entities-filters">
                {
                    showFilters && (
                        <div className="filters-section">
                            <div className="review-screen-filters-section">
                                <div className="input-wrapper">
                                    <div className="input-label">
                                        Assessment Type
                                    </div>
                                    <div className="input-container">
                                        <Select
                                            showSearch
                                            allowClear
                                            className="filter-select"
                                            options={assessmentTypeOptions}
                                            onChange={handleAssessmentTypeChange}
                                            value={selectedAssessmentType}
                                            placeholder="Select Type"
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                        />
                                    </div>
                                </div>
                                <div className="input-wrapper">
                                    <div className="input-label">
                                        Entity
                                    </div>
                                    <div className="input-container">
                                        <Select
                                            maxTagCount={1}
                                            mode="multiple"
                                            showSearch
                                            allowClear
                                            className="filter-select"
                                            options={entityDropdownData.responseData}
                                            onChange={handleEntityChange}
                                            value={selectedEntity}
                                            placeholder="Select Entity"
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                            notFoundContent={entityLoading ? <div className="dropdown-loading"><Spin size="small" /></div> : <div className="dropdown-loading">No Data</div>}
                                        />
                                    </div>
                                </div>
                                <div className="input-wrapper">
                                    <div className="input-label">
                                        Batch Status
                                    </div>
                                    <div className="input-container">
                                        <Select
                                            showSearch
                                            allowClear
                                            className="filter-select"
                                            options={batchStatusOptions}
                                            onChange={handleBatchStatus}
                                            value={selectedBatchStatus}
                                            placeholder="Select Status"
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                        />
                                    </div>
                                </div>
                                <div className="input-wrapper">
                                    <div className="input-label">
                                        Publish Status
                                    </div>
                                    <div className="input-container">
                                        <Select
                                            showSearch
                                            allowClear
                                            className="filter-select"
                                            options={publishTaskOptions}
                                            onChange={handlePublishTaskStatusChange}
                                            value={selectedPublishTaskStatus}
                                            placeholder="Select Status"
                                            filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                                        />
                                    </div>
                                </div>
                                <div className="filter-buttons-wrapper">
                                    <div className={`button-wrapper ${(selectedEntity?.length === 0 && !selectedBatchStatus && !selectedPublishTaskStatus && !selectedAssessmentType) || itemLevelDataLoading ? "export-disabled" : ""}`} onClick={() => handleApplyFilters()}>Apply</div>
                                    <div className="button-wrapper" onClick={() => handleResetFilters()}>Reset</div>
                                </div>
                                <div className="filter-buttons-section content-buttons-left">
                                    <div className={`button-wrapper ${(importCorrectedFileLoading || (selectedBatchStatus && selectedBatchStatus !== "Completed") || (trackExportStatus?.exportAll && exportExtractionLoading) || selectedRowKeys?.length > 0) ? "export-disabled" : ""}`} onClick={() => { handleExportReport("exportAll") }}>
                                        {(exportExtractionLoading && trackExportStatus?.exportAll) ? <Spin className="export" indicator={antIcon} /> : <PiExportBold />}
                                        Export All
                                    </div>
                                    <div className={`button-wrapper ${(importCorrectedFileLoading || (trackExportStatus?.all && exportExtractionLoading) || selectedRowKeys?.length === 0) ? "export-disabled" : ""}`} onClick={() => handleExportReport("All")}>
                                        {(exportExtractionLoading && trackExportStatus?.all) ? <Spin className="export" indicator={antIcon} /> : <PiExportBold />}
                                        Export Selected
                                    </div>
                                    <div className={`button-wrapper ${(importCorrectedFileLoading || (selectedBatchStatus && selectedBatchStatus !== "Completed") || (trackExportStatus?.exportConflictsAll && exportExtractionLoading) || selectedRowKeys?.length > 0) ? "export-disabled" : ""}`} onClick={() => { handleExportReport("exportConflictsAll") }}>
                                        {(exportExtractionLoading && trackExportStatus?.exportConflictsAll) ? <Spin className="export" indicator={antIcon} /> : <PiExportBold />}
                                        Export All Conflicts
                                    </div>
                                    <div className={`button-wrapper ${(importCorrectedFileLoading || (trackExportStatus?.conflict && exportExtractionLoading) || selectedRowKeys?.length === 0) ? "export-disabled" : ""}`} onClick={() => handleExportReport("Conflicts")}>
                                        {(exportExtractionLoading && trackExportStatus?.conflict) ? <Spin className="export" indicator={antIcon} /> : <PiExportBold />}
                                        Export Selected Conflicts
                                    </div>
                                    <div className={`button-wrapper ${(selectedRowKeys?.length === 0) ? "export-disabled" : ""}`} onClick={handlePublishTask}>
                                        {publishTaskLoading ? <Spin className="export" indicator={antIcon} /> : <MdPublish />}
                                        Publish
                                    </div>
                                    <div className="button-wrapper" onClick={handleImport}>
                                        {importCorrectedFileLoading ? <Spin className="export" indicator={antIcon} /> : <BiImport />}
                                        Import
                                    </div>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".xlsx"
                                        style={{ display: "none" }}
                                        onChange={(e) => handleImportFile(e)}
                                    />
                                </div>
                            </div>

                        </div>
                    )
                }
            </div>
            <div className="review-entities-table-section">
                <CustomTable
                    page={page}
                    setPage={setPage}
                    limit={limit}
                    setLimit={setLimit}
                    tableData={formattedTableData}
                    isLoading={itemLevelDataLoading}
                    totalRows={itemLevelData?.totalCount}
                    rowSelection={rowSelection}
                />
            </div>
            {showImportModal && (
                <CustomModal
                    showCloseIcon
                    handleClose={() => setShowImportModal(false)}
                    modalTitle={
                        <div className="modal-title">
                            <div className="modal-icon">
                                <PiWarningCircleLight />
                            </div>
                            Confirm
                        </div>
                    }
                >
                    <ConfirmationModal
                        handleClose={() => setShowImportModal(false)}
                        handleConfirm={handleConfirmImport}
                        message={
                            <>
                                Are you sure you want to import <strong>{importFileName || " "}</strong>?
                            </>
                        }
                    />
                </CustomModal>
            )}
        </div>
    )
}

export default OutputReview;